import { RefObject, useEffect } from 'react';

import { isValueSet } from '@utils/guards';

type ClickOutsideOptions<T extends HTMLElement> = {
    ref: RefObject<T>;
    action(): void;
    additionalTriggerChecker?(target: EventTarget | null): boolean;
};

export const useClickOutside = <T extends HTMLElement>(options: ClickOutsideOptions<T>) => {
    const { ref, action, additionalTriggerChecker = () => true } = options;

    useEffect(() => {
        if (isValueSet(ref.current)) ref.current.focus();

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target;

            if (
                isValueSet(ref.current) &&
                (!(target instanceof HTMLElement) || !ref.current.contains(target)) &&
                additionalTriggerChecker(target)
            ) {
                action();
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [action, additionalTriggerChecker, ref]);
};
